import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Bike, MapPin, Clock, Phone, Check, Package, DollarSign,
  Navigation, Power, ChefHat, Home, Wallet
} from "lucide-react";
import { toast } from "sonner";

const PHONE_ICON_CLASS = "w-3.5 h-3.5";

export default function Driver() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("available");
  const [available, setAvailable] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [earnings, setEarnings] = useState({ total_earnings: 0, delivered_count: 0 });
  const [online, setOnline] = useState(false);
  const watchIdRef = useRef(null);

  const loadAll = async () => {
    try {
      const [a, m, h, e] = await Promise.all([
        api.get("/driver/orders/available"),
        api.get("/driver/orders/my"),
        api.get("/driver/orders/history"),
        api.get("/driver/earnings"),
      ]);
      setAvailable(a.data); setMyOrders(m.data); setHistory(h.data); setEarnings(e.data);
    } catch {}
  };

  useEffect(() => {
    if (user?.is_driver) {
      loadAll();
      const t = setInterval(loadAll, 15000);
      return () => clearInterval(t);
    }
  }, [user]);

  // GPS watcher - posts location every 30s when online
  useEffect(() => {
    if (!online || !user?.is_driver) {
      if (watchIdRef.current) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
      return;
    }
    if (!navigator.geolocation) { toast.error("المتصفح لا يدعم GPS"); return; }
    let lastSend = 0;
    const success = (pos) => {
      const now = Date.now();
      if (now - lastSend < 30000) return; // throttle 30s
      lastSend = now;
      api.post("/driver/location", { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
    };
    const id = navigator.geolocation.watchPosition(success, () => {}, { enableHighAccuracy: true });
    watchIdRef.current = id;
    return () => { if (id) navigator.geolocation.clearWatch(id); };
  }, [online, user]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#E05D3A] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user || !user.is_driver) return <Navigate to="/login" replace />;

  const toggleOnline = async () => {
    const newStatus = online ? "offline" : "available";
    try {
      await api.post("/driver/status", { status: newStatus });
      setOnline(!online);
      toast.success(newStatus === "available" ? "أنت متاح الآن" : "أنت غير متاح");
    } catch { toast.error("فشل"); }
  };

  const accept = async (oid) => {
    try {
      const { data } = await api.post(`/driver/orders/${oid}/accept`);
      toast.success(`تم القبول — عمولتك: ${data.commission} SAR`);
      await loadAll();
      setTab("active");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل القبول");
    }
  };

  const updateStatus = async (oid, status) => {
    try {
      await api.put(`/driver/orders/${oid}/status`, { status });
      toast.success(status === "on_the_way" ? "في الطريق" : "تم التوصيل");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
  };

  const openMaps = (addr) => {
    const query = encodeURIComponent(`${addr.street || ""}, ${addr.city || ""}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
  };

  return (
    <div className="bg-[#F5F5F5] min-h-screen pb-20 md:pb-6">
      {/* Top bar */}
      <section className="bg-white border-b border-[#EAEAEA]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full bg-[#E05D3A] text-white flex items-center justify-center">
              <Bike className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-[#1A1A1A] truncate">{user.name}</h1>
              <p className="text-xs text-[#6B6B6B]">
                {user.vehicle_type === "motorcycle" ? "🏍️ دراجة نارية" : user.vehicle_type === "car" ? "🚗 سيارة" : "🚴 دراجة"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-end">
              <p className="text-[10px] text-[#6B6B6B]">الحالة</p>
              <Badge className={online ? "bg-[#00A85A] text-white" : "bg-gray-300 text-gray-700"}>
                {online ? "متاح" : "غير متاح"}
              </Badge>
            </div>
            <Switch checked={online} onCheckedChange={toggleOnline} data-testid="driver-online-toggle" />
          </div>
        </div>
      </section>

      {/* Earnings summary */}
      <section className="max-w-4xl mx-auto px-4 py-4 grid grid-cols-3 gap-3">
        <StatCard icon={<Wallet className="w-5 h-5" />} label="الأرباح" value={`${earnings.total_earnings} ر`} accent />
        <StatCard icon={<Check className="w-5 h-5" />} label="تم التوصيل" value={earnings.delivered_count} />
        <StatCard icon={<Package className="w-5 h-5" />} label="نشطة" value={myOrders.length} />
      </section>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-4 flex gap-1 border-b border-[#EAEAEA] mb-4 sticky top-0 bg-[#F5F5F5] z-10">
        {[
          { id: "available", label: `المتاحة (${available.length})`, testId: "driver-tab-available" },
          { id: "active", label: `طلباتي (${myOrders.length})`, testId: "driver-tab-active" },
          { id: "history", label: "السجل", testId: "driver-tab-history" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px ${tab === t.id ? "border-[#E05D3A] text-[#E05D3A]" : "border-transparent text-[#6B6B6B]"}`}
            data-testid={t.testId}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="max-w-4xl mx-auto px-4 space-y-3">
        {tab === "available" && (
          available.length === 0 ? (
            <div className="jh-card p-10 text-center text-[#6B6B6B]">
              <ChefHat className="w-12 h-12 mx-auto text-[#E05D3A]/60" />
              <p className="mt-3">لا توجد طلبات متاحة حاليًا</p>
              <p className="text-xs mt-1">سنعلمك فور وصول طلب جديد</p>
            </div>
          ) : available.map((o) => (
            <OrderCard key={o.order_id} o={o} type="available" onAccept={() => accept(o.order_id)} openMaps={openMaps} />
          ))
        )}

        {tab === "active" && (
          myOrders.length === 0 ? (
            <div className="jh-card p-10 text-center text-[#6B6B6B]">لا توجد طلبات نشطة — اقبل طلبًا من التبويب "المتاحة"</div>
          ) : myOrders.map((o) => (
            <OrderCard key={o.order_id} o={o} type="active" onStatus={updateStatus} openMaps={openMaps} />
          ))
        )}

        {tab === "history" && (
          history.length === 0 ? (
            <div className="jh-card p-10 text-center text-[#6B6B6B]">لم تكمل أي طلب بعد</div>
          ) : history.map((o) => (
            <OrderCard key={o.order_id} o={o} type="history" />
          ))
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div className={`jh-card p-3 text-center ${accent ? "bg-gradient-to-br from-[#E05D3A] to-[#C24B2C] text-white border-0" : ""}`}>
      <div className={`${accent ? "text-white/80" : "text-[#E05D3A]"} flex justify-center`}>{icon}</div>
      <p className={`text-[10px] ${accent ? "text-white/80" : "text-[#6B6B6B]"} mt-1`}>{label}</p>
      <p className={`font-bold text-base mt-0.5 ${accent ? "text-white" : "text-[#1A1A1A]"}`}>{value}</p>
    </div>
  );
}

function OrderCard({ o, type, onAccept, onStatus, openMaps }) {
  const commission = o.driver_commission || (o.total * 0.15).toFixed(2);
  return (
    <div className="jh-card p-4" data-testid={`driver-order-${o.order_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-[10px] text-[#6B6B6B]">#{o.order_id.slice(-6).toUpperCase()}</p>
          <h3 className="font-bold text-[#1A1A1A] truncate">{o.restaurant_name_ar}</h3>
          <p className="text-xs text-[#6B6B6B] mt-0.5">{o.items.length} عنصر · {o.payment_method === "cash" ? "💵 كاش" : "💳 بطاقة"}</p>
        </div>
        <div className="text-end flex-shrink-0">
          <p className="text-[10px] text-[#6B6B6B]">العمولة</p>
          <p className="font-bold text-[#00A85A]">{commission} ر</p>
          <p className="text-[10px] text-[#6B6B6B] mt-0.5">الإجمالي: {o.total.toFixed(2)}</p>
        </div>
      </div>

      <div className="bg-[#F5F5F5] rounded-xl p-3 flex items-start gap-2 mb-3">
        <MapPin className="w-4 h-4 text-[#E05D3A] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0 text-sm">
          <p className="font-semibold text-[#1A1A1A]">{o.address?.label}</p>
          <p className="text-xs text-[#6B6B6B] truncate">{o.address?.street}، {o.address?.city}</p>
          {o.address?.notes && <p className="text-xs text-[#6B6B6B] mt-0.5">📝 {o.address.notes}</p>}
        </div>
        {openMaps && (
          <button onClick={() => openMaps(o.address)} className="flex-shrink-0 text-xs text-[#E05D3A] font-semibold flex items-center gap-1" data-testid={`driver-navigate-${o.order_id}`}>
            <Navigation className="w-3.5 h-3.5" /> توجيه
          </button>
        )}
      </div>

      {/* Actions */}
      {type === "available" && (
        <Button onClick={onAccept} className="w-full rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white" data-testid={`driver-accept-${o.order_id}`}>
          <Check className="w-4 h-4 me-2" /> اقبل الطلب
        </Button>
      )}
      {type === "active" && (
        <div className="grid grid-cols-2 gap-2">
          {o.status === "preparing" ? (
            <Button onClick={() => onStatus(o.order_id, "on_the_way")} className="col-span-2 rounded-full bg-[#1A1A1A] hover:bg-[#333] text-white" data-testid={`driver-pickup-${o.order_id}`}>
              استلمت الطلب (في الطريق)
            </Button>
          ) : (
            <Button onClick={() => onStatus(o.order_id, "delivered")} className="col-span-2 rounded-full bg-[#00A85A] hover:bg-[#00844A] text-white" data-testid={`driver-deliver-${o.order_id}`}>
              <Check className="w-4 h-4 me-2" /> تم التوصيل
            </Button>
          )}
        </div>
      )}
      {type === "history" && (
        <div className="flex items-center justify-between text-xs text-[#6B6B6B]">
          <span>{new Date(o.driver_delivered_at || o.created_at).toLocaleDateString("ar-SA")}</span>
          {o.driver_rating && <span>⭐ {o.driver_rating}/5</span>}
        </div>
      )}
    </div>
  );
}
